// Files contain tailwind class names should have tsx extension for tailwind to scan

import { clsx } from "clsx";

import { BRANDING_VARS } from "../constants";

export type ButtonSizes = "default" | "small";

const sizeClasses: Record<ButtonSizes, string> = {
  default: "py-[0.9rem]",
  small: "py-[0.5rem]",
};

const revertFocusBorderClasses =
  "active:before:inset-0 active:before:border-0 active:before:rounded-none";

type BaseProps = {
  size?: ButtonSizes;
  rounded?: boolean;
  branded?: boolean;
};
const base = ({ size, rounded, branded }: BaseProps) => {
  let radiusClass = "rounded";

  if (rounded) {
    radiusClass = "rounded-large";
  } else if (branded) {
    radiusClass = `rounded-[var(${BRANDING_VARS.buttonBrandRadius})]`;
  }

  return clsx(
    "relative flex justify-center items-center gap-[0.8rem] px-[1.6rem] rounded focus-visible:outline-none disabled:cursor-not-allowed",
    size && sizeClasses[size],
    radiusClass,
  );
};

export const focusBorder = (rounded?: boolean, branded?: boolean) => {
  let radiusClass = "focus:before:rounded";

  if (rounded) {
    radiusClass = "focus:before:rounded-full";
  } else if (branded) {
    radiusClass = `focus:before:rounded-[var(${BRANDING_VARS.buttonBrandRadius})]`;
  }

  return clsx(
    "focus:before:absolute focus:before:inset-[-0.4rem] focus:before:border focus:before:border-solid focus:before:border-blue-560",
    radiusClass,
  );
};

const primaryDefaultBase = (props: BaseProps) =>
  clsx(
    base(props),
    "text-white border-solid border",
    props.branded
      ? `bg-[var(${BRANDING_VARS.buttonBrandColor})] border-[var(${BRANDING_VARS.buttonBrandColor})]`
      : "bg-blue-560 border-blue-560",
  );
const primaryDefaultFocus = (
  rounded?: boolean,
  isPressed?: boolean,
  branded?: boolean,
) =>
  clsx(
    focusBorder(rounded, branded),
    !isPressed &&
      (branded
        ? `focus:bg-[var(${BRANDING_VARS.buttonBrandColorHover})]`
        : "focus:bg-blue-460"),
  );
const primaryDefaultHover = (branded?: boolean) =>
  branded
    ? `hover:bg-[var(${BRANDING_VARS.buttonBrandColorHover})]`
    : "hover:bg-blue-460";
const primaryDefaultActive = (branded?: boolean) =>
  branded
    ? `active:bg-[var(${BRANDING_VARS.buttonBrandColorActive})]`
    : "active:bg-blue-660";
const primaryDefaultDisabled =
  "disabled:bg-black/5 disabled:text-neutral-400 disabled:border-none";

const destructiveDefaultBase = (props: BaseProps) =>
  clsx(base(props), "text-white bg-red-550");
const destructiveDefaultFocus = (
  rounded?: boolean,
  isPressed?: boolean,
  branded?: boolean,
) => clsx(focusBorder(rounded, branded), !isPressed && "focus:bg-red-450");
const destructiveDefaultHover = "hover:bg-red-450";
const destructiveDefaultActive = "active:bg-red-650";
const destructiveDefaultDisabled =
  "disabled:bg-black/5 disabled:text-neutral-400 disabled:border-none";

const destructiveSubtleBase = (props: BaseProps) =>
  clsx(base(props), "text-red-550");
const destructiveSubtleFocus = (
  rounded?: boolean,
  isPressed?: boolean,
  branded?: boolean,
) => clsx(focusBorder(rounded, branded), !isPressed && "focus:bg-red-50");
const destructiveSubtleHover = "hover:bg-red-50";
const destructiveSubtleActive = clsx(
  "active:bg-red-650 active:text-white",
  revertFocusBorderClasses,
);
const destructiveSubtleDisabled = "disabled:bg-white disabled:text-neutral-400";

const primarySubtleBase = (props: BaseProps) =>
  clsx(
    base(props),
    "bg-white border-solid border",
    props.branded
      ? `text-[var(${BRANDING_VARS.buttonBrandColor})] border-[var(${BRANDING_VARS.buttonBrandColor})]`
      : "text-blue-560 border-blue-360",
  );
const primarySubtleFocus = (
  rounded?: boolean,
  isPressed?: boolean,
  branded?: boolean,
) =>
  clsx(
    focusBorder(rounded, branded),
    !isPressed &&
      (branded
        ? `focus:bg-[var(${BRANDING_VARS.buttonBrandColorSubtleHover})]`
        : "focus:bg-blue-60"),
  );
const primarySubtleHover = (branded?: boolean) =>
  branded
    ? `hover:border-[var(${BRANDING_VARS.buttonBrandColorHover})] hover:bg-[var(${BRANDING_VARS.buttonBrandColorSubtleHover})]`
    : "hover:border-blue-560 hover:bg-blue-60";
const primarySubtleActive = (branded?: boolean) =>
  branded
    ? `active:bg-[var(${BRANDING_VARS.buttonBrandColor})] active:border-[var(${BRANDING_VARS.buttonBrandColor})] active:text-white`
    : "active:bg-blue-660 active:border-blue-560 active:text-white";
const primarySubtleDisabled =
  "disabled:bg-white disabled:border-neutral-400 disabled:text-neutral-400";

const secondaryDefaultBase = (props: BaseProps) =>
  clsx(
    base(props),
    "text-neutral-850 bg-white border-solid border border-neutral-400",
  );
const secondaryDefaultFocus = (
  rounded?: boolean,
  isPressed?: boolean,
  branded?: boolean,
) => clsx(focusBorder(rounded, branded), !isPressed && "focus:bg-neutral-50");
const secondaryDefaultHover = "hover:bg-neutral-50";
const secondaryDefaultActive =
  "active:bg-neutral-100 active:border-neutral-100";
const secondaryDefaultDisabled =
  "disabled:bg-white disabled:border-neutral-400 disabled:text-neutral-400";

const secondarySubtleBase = (props: BaseProps) =>
  clsx(base(props), "text-neutral-850");
const secondarySubtleFocus = (
  rounded?: boolean,
  isPressed?: boolean,
  branded?: boolean,
) => clsx(focusBorder(rounded, branded), !isPressed && "focus:bg-neutral-50");
const secondarySubtleHover = "hover:bg-black/5";
const secondarySubtleActive = "active:bg-black/10";
const secondarySubtleDisabled = "disabled:bg-white disabled:text-neutral-400";

const highlightBase = (props: BaseProps) =>
  clsx(
    base(props),
    "bg-white px-[0.4rem]",
    props.branded
      ? `text-[var(${BRANDING_VARS.buttonBrandColor})]`
      : "text-blue-560",
  );
const highlightFocus = (
  rounded?: boolean,
  isPressed?: boolean,
  branded?: boolean,
) =>
  clsx(
    focusBorder(rounded, branded),
    !isPressed &&
      (branded
        ? `focus:bg-[var(${BRANDING_VARS.buttonBrandColorSubtleHover})]`
        : "focus:bg-blue-60"),
  );
const highlightHover = (branded?: boolean) =>
  branded
    ? `hover:bg-[var(${BRANDING_VARS.buttonBrandColorSubtleHover})]`
    : "hover:bg-blue-60";
const highlightActive = (branded?: boolean) =>
  branded
    ? `active:bg-[var(${BRANDING_VARS.buttonBrandColorActive})] active:text-white`
    : "active:bg-blue-660 active:text-white";
const highlightDisabled = "disabled:bg-white disabled:text-neutral-400";

const invertedDefaultBase = (props: BaseProps) =>
  clsx(base(props), "text-white border-solid border border-white/40");
const invertedDefaultFocus = (
  rounded?: boolean,
  isPressed?: boolean,
  branded?: boolean,
) => clsx(focusBorder(rounded, branded), !isPressed && "focus:bg-white/10");
const invertedDefaultHover = "hover:bg-white/10";
const invertedDefaultActive = "active:bg-white/20 active:border-white/0";
const invertedDefaultDisabled =
  "disabled:text-white/40 disabled:bg-white/0 disabled:border-white/40";

const invertedSubtleBase = (props: BaseProps) =>
  clsx(base(props), "text-white border-solid border border-white/0");
const invertedSubtleFocus = (
  rounded?: boolean,
  isPressed?: boolean,
  branded?: boolean,
) => clsx(focusBorder(rounded, branded), !isPressed && "focus:bg-white/10");
const invertedSubtleHover = "hover:bg-white/10";
const invertedSubtleActive = "active:bg-white/20";
const invertedSubtleDisabled = "disabled:text-white/40 disabled:bg-white/0";

const inlineDefaultBase = (props: BaseProps) =>
  clsx(
    base(props),
    "text-neutral-850 px-[1.2rem] py-[0.12rem] rounded-small border-solid border border-neutral-400",
  );
const inlineDefaultFocus = (
  rounded?: boolean,
  isPressed?: boolean,
  branded?: boolean,
) =>
  clsx(
    focusBorder(rounded, branded),
    !isPressed && "focus:bg-neutral-50",
    "focus:before:rounded-small",
  );
const inlineDefaultHover = "hover:bg-neutral-50";
const inlineDefaultActive = "active:bg-neutral-100 active:border-neutral-100";
const inlineDefaultDisabled =
  "disabled:text-neutral-400 disabled:bg-white/0 disabled:border-neutral-400";

const inlineSubtleBase = (props: BaseProps) =>
  clsx(inlineDefaultBase(props), "border-white/0");
const inlineSubtleFocus = inlineDefaultFocus;
const inlineSubtleHover = inlineDefaultHover;
const inlineSubtleActive = inlineDefaultActive;
const inlineSubtleDisabled =
  "disabled:text-neutral-400 disabled:bg-white/0 disabled:border-white/0";

export type ButtonVariant =
  | "primaryDefault"
  | "primarySubtle"
  | "destructiveDefault"
  | "destructiveSubtle"
  | "secondaryDefault"
  | "secondarySubtle"
  | "highlight"
  | "invertedDefault"
  | "invertedSubtle"
  | "inlineDefault"
  | "inlineSubtle";

const extractActiveClasses = (classes: string) =>
  classes.replace(/active:/g, "");

const getActiveClasses = (variant: ButtonVariant, branded?: boolean) => {
  switch (variant) {
    case "primaryDefault":
      return extractActiveClasses(primaryDefaultActive(branded));
    case "primarySubtle":
      return extractActiveClasses(primarySubtleActive(branded));
    case "destructiveDefault":
      return extractActiveClasses(destructiveDefaultActive);
    case "destructiveSubtle":
      return extractActiveClasses(secondarySubtleActive);
    case "secondaryDefault":
      return extractActiveClasses(secondaryDefaultActive);
    case "secondarySubtle":
      return extractActiveClasses(secondarySubtleActive);
    case "highlight":
      return extractActiveClasses(highlightActive(branded));
    case "invertedDefault":
      return extractActiveClasses(invertedDefaultActive);
    case "invertedSubtle":
      return extractActiveClasses(invertedSubtleActive);
    case "inlineDefault":
      return extractActiveClasses(inlineDefaultActive);
    case "inlineSubtle":
      return extractActiveClasses(inlineSubtleActive);
    default:
      return "";
  }
};

export const getVariantClassNames = ({
  loading,
  variant,
  size,
  rounded,
  isPressed,
  branded,
}: {
  loading: boolean | undefined;
  variant: ButtonVariant;
  size: ButtonSizes;
  rounded?: boolean;
  isPressed?: boolean;
  branded?: boolean;
}) => {
  const buttonVariants = {
    primaryDefault: clsx(
      primaryDefaultBase({ size, rounded, branded }),
      !loading && [
        primaryDefaultFocus(rounded, isPressed, branded),
        primaryDefaultHover(branded),
        primaryDefaultActive(branded),
        primaryDefaultDisabled,
        isPressed && getActiveClasses(variant, branded),
      ],
    ),
    destructiveDefault: clsx(
      destructiveDefaultBase({ size, rounded, branded }),
      !loading && [
        destructiveDefaultFocus(rounded, isPressed, branded),
        destructiveDefaultHover,
        destructiveDefaultActive,
        destructiveDefaultDisabled,
        isPressed && getActiveClasses(variant, branded),
      ],
    ),
    destructiveSubtle: clsx(
      destructiveSubtleBase({ size, rounded, branded }),
      !loading && [
        destructiveSubtleFocus(rounded, isPressed, branded),
        destructiveSubtleHover,
        destructiveSubtleActive,
        destructiveSubtleDisabled,
      ],
    ),
    primarySubtle: clsx(
      primarySubtleBase({ size, rounded, branded }),
      !loading && [
        primarySubtleFocus(rounded, isPressed, branded),
        primarySubtleHover(branded),
        primarySubtleActive(branded),
        primarySubtleDisabled,
        isPressed && getActiveClasses(variant, branded),
      ],
    ),
    secondaryDefault: clsx(
      secondaryDefaultBase({ size, rounded, branded }),
      sizeClasses[size],
      !loading && [
        secondaryDefaultFocus(rounded, isPressed, branded),
        secondaryDefaultHover,
        secondaryDefaultActive,
        secondaryDefaultDisabled,
        isPressed && getActiveClasses(variant, branded),
      ],
    ),
    secondarySubtle: clsx(
      secondarySubtleBase({ size, rounded, branded }),
      !loading && [
        secondarySubtleFocus(rounded, isPressed, branded),
        secondarySubtleHover,
        secondarySubtleActive,
        secondarySubtleDisabled,
        isPressed && getActiveClasses(variant, branded),
      ],
    ),
    highlight: clsx(
      highlightBase({ size, rounded, branded }),
      !loading && [
        highlightFocus(rounded, isPressed, branded),
        highlightHover(branded),
        highlightActive(branded),
        highlightDisabled,
        isPressed && getActiveClasses(variant, branded),
      ],
    ),
    invertedDefault: clsx(
      invertedDefaultBase({ size, rounded, branded }),
      !loading && [
        invertedDefaultFocus(rounded, isPressed, branded),
        invertedDefaultHover,
        invertedDefaultActive,
        invertedDefaultDisabled,
        isPressed && getActiveClasses(variant, branded),
      ],
    ),
    invertedSubtle: clsx(
      invertedSubtleBase({ size, rounded, branded }),
      !loading && [
        invertedSubtleFocus(rounded, isPressed, branded),
        invertedSubtleHover,
        invertedSubtleActive,
        invertedSubtleDisabled,
        isPressed && getActiveClasses(variant, branded),
      ],
    ),
    inlineDefault: clsx(
      inlineDefaultBase({ rounded }),
      !loading && [
        inlineDefaultFocus(rounded, isPressed),
        inlineDefaultHover,
        inlineDefaultActive,
        inlineDefaultDisabled,
        isPressed && getActiveClasses(variant, branded),
      ],
    ),
    inlineSubtle: clsx(
      inlineSubtleBase({ rounded }),
      !loading && [
        inlineSubtleFocus(rounded, isPressed),
        inlineSubtleHover,
        inlineSubtleActive,
        inlineSubtleDisabled,
        isPressed && getActiveClasses(variant, branded),
      ],
    ),
  };

  return buttonVariants[variant];
};

export const getSingleIconClassNames = (
  variant: ButtonVariant,
  size: ButtonSizes,
) => {
  if (["inlineDefault", "inlineSubtle"].includes(variant))
    return "p-[0.325rem]";
  return size === "default"
    ? "p-[1.2rem] max-h-16 max-w-16"
    : "p-[0.8rem] max-h-[3.2rem] max-w-[3.2rem]";
};
